import React, { useCallback, useEffect, useState } from "react";
import { Box, Paper, Typography, Button, Chip, CircularProgress, IconButton, Tooltip, Collapse, TextField, MenuItem } from "@mui/material";
import { Add, SwapVert, Delete, ExpandMore, ExpandLess } from "@mui/icons-material";
import { colors, typography } from "../theme/designSystem";
import { filterFieldSx, searchFieldSx, dateFieldSx, filterActionButtonSx } from '../theme/filterStyles';
import {
  patientTimelineService,
  PatientTimeline as TimelineData,
  TimelineEvent,
  TimelineKind,
} from "../services/patientTimelineService";
import TreatmentOutcomeModal from "./modals/TreatmentOutcomeModal";
import ConfirmModal from "./modals/ConfirmModal";
import ClearFiltersButton from './ClearFiltersButton';
import ReportSummaryCard, { summaryCardsRowSx } from "./Reports/ReportSummaryCard";

/** Mesma régua de inativo da Visão geral (RN-528a). */
const INACTIVE_DAYS = 180;

// Cor por tipo de marco. Cinza é o default de rotina — só o que muda o rumo do tratamento
// ganha cor, senão a linha inteira vira arco-íris e nada se destaca.
/** Rótulos dos marcos no combo de filtro — os mesmos nomes que a lista usa. */
const KIND_LABEL: Record<TimelineKind, string> = {
  registration: "Cadastro",
  specialty_start: "Início de especialidade",
  evaluation: "Avaliação",
  plan: "Plano terapêutico",
  sessions: "Sessões",
  absence: "Ausência",
  return: "Retorno",
  professional_change: "Troca de profissional",
  frequency_change: "Mudança de frequência",
  inactive: "Inativo",
  outcome: "Desfecho",
  link: "Vínculo",
};

const KIND_COLOR: Record<TimelineKind, string> = {
  registration: colors.info,
  specialty_start: colors.info,
  evaluation: colors.primary,
  plan: colors.info,
  sessions: colors.border,
  absence: colors.error,
  return: colors.success,
  professional_change: colors.warning,
  frequency_change: colors.warning,
  // Inativo é situação deduzida, não desfecho registrado: cinza para não competir com o
  // que a equipe de fato informou.
  inactive: colors.textMuted,
  outcome: colors.success,
  // Vínculo é contexto de vida, não evento clínico: cor própria e discreta, para situar
  // sem competir com sessão, avaliação e desfecho.
  link: "#607d8b",
};

// Botão de ação das abas do cadastro: quadrado 40x40 com borda ciano, só ícone.


interface Props {
  patientId: string;
  entityId: string;
}

const PatientTimeline: React.FC<Props> = ({ patientId, entityId }) => {
  const [data, setData] = useState<TimelineData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sort, setSort] = useState<"asc" | "desc">("desc");
  const [monthsBack, setMonthsBack] = useState(24);
  const [kindFilter, setKindFilter] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [toDelete, setToDelete] = useState<TimelineEvent | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await patientTimelineService.get(patientId, monthsBack, sort, from, to));
    } catch {
      setError("Não foi possível carregar a linha do tempo.");
    } finally {
      setLoading(false);
    }
  }, [patientId, monthsBack, sort, from, to]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!toDelete?.outcomeId) return;
    await patientTimelineService.deleteOutcome(toDelete.outcomeId);
    setToDelete(null);
    load();
  };

  const s = data?.summary;

  // As opções do modal são as especialidades QUE ESTE PACIENTE faz, vindas do próprio resumo.
  // Antes daqui vinham do catálogo da entidade, cujo endpoint exige permissão de configuração
  // que nem Profissional nem Administrador têm — a chamada dava 403 e a lista ficava vazia.
  const specialties = (s?.specialtyStatus ?? [])
    .filter((st) => !!st.specialtyId)
    .map((st) => ({ id: st.specialtyId as string, name: st.specialty }));

  // Tipo e especialidade recortam o que já veio (a data é que vai ao servidor). Os tipos do
  // combo saem dos eventos deste paciente: oferecer marco que ele não tem só gera lista vazia.
  const kindsPresent = Array.from(new Set((data?.events ?? []).map((e) => e.kind)));
  const events = (data?.events ?? []).filter((e) =>
    (!kindFilter || e.kind === kindFilter)
    && (!specialtyFilter || e.specialty === specialtyFilter));
  const hasFilters = !!(kindFilter || specialtyFilter || from || to);

  return (
    <div className="tab-content-section">
      {/* Padrão das abas (RN-539): sem título e sem caixa informativa — o que a aba faz e
          suas regras vivem no FAQ, alcançável pelo botão de ajuda. */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mb: 2 }}>
        <TextField
          select
          size="small"
          label="Marco"
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Todos</MenuItem>
          {kindsPresent.map((k) => (
            <MenuItem key={k} value={k}>{KIND_LABEL[k]}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Especialidade"
          value={specialtyFilter}
          onChange={(e) => setSpecialtyFilter(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Todas</MenuItem>
          {specialties.map((sp) => (
            <MenuItem key={sp.id} value={sp.name}>{sp.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="date"
          label="De"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />
        <TextField
          size="small"
          type="date"
          label="Até"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />
        <ClearFiltersButton
          onClick={() => { setKindFilter(""); setSpecialtyFilter(""); setFrom(""); setTo(""); setMonthsBack(24); }}
          disabled={!hasFilters}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, marginLeft: "auto" }}>
          <Tooltip
            arrow
            title={sort === "desc"
              ? "Do mais recente para o mais antigo (clique para inverter)"
              : "Do mais antigo para o mais recente (clique para inverter)"}
          >
            <IconButton onClick={() => setSort((v) => (v === "desc" ? "asc" : "desc"))} sx={filterActionButtonSx}>
              <SwapVert />
            </IconButton>
          </Tooltip>
          <Tooltip title="Registrar marco" arrow>
            <IconButton onClick={() => setModalOpen(true)} sx={filterActionButtonSx}>
              <Add />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Indicadores nos cards de resumo canônicos (mesmos do Dashboard/relatórios). A quebra dos
          cancelamentos e as sessões sem presença registrada vivem DENTRO dos cards a que
          pertencem — antes eram linhas soltas abaixo da grade. */}
      {s && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ ...summaryCardsRowSx, mb: s.specialtyStatus.length > 0 ? 1.5 : 0 }}>
            <ReportSummaryCard
              label="Primeiro atendimento"
              value={s.firstContact || "—"}
              sub={s.specialties > 0 ? `${s.specialties} ${s.specialties === 1 ? "especialidade" : "especialidades"}` : undefined}
            />
            <ReportSummaryCard
              label="Agendamentos"
              value={String(s.scheduled)}
              sub={s.withoutRecord > 0 ? `${s.withoutRecord} sem presença registrada` : undefined}
              subColor={colors.warning}
            />
            <ReportSummaryCard label="Compareceu" value={String(s.totalAttendances)} color={colors.success} />
            <ReportSummaryCard
              label="Faltas"
              value={String(s.noShows)}
              color={s.noShows > 0 ? colors.error : undefined}
            />
            <ReportSummaryCard
              label="Cancelamentos"
              value={String(s.cancelledByPatient + s.cancelledByProfessional + s.cancelledByClinic)}
              color={colors.warning}
              sub={`paciente ${s.cancelledByPatient} · profissional ${s.cancelledByProfessional} · feriado/recesso ${s.cancelledByClinic}`}
            />
            <ReportSummaryCard
              label="Aproveitamento"
              value={`${s.attendanceRate}%`}
              color={s.attendanceRate < 70 ? colors.error : colors.success}
              sub="presenças ÷ (presenças + faltas + canc. do paciente + sem registro)"
              note={s.withoutRecord > 0 ? "sem presença registrada conta como não aproveitada" : undefined}
            />
            <ReportSummaryCard
              label="Desde a última sessão"
              value={s.daysSinceLastAttendance != null ? `${s.daysSinceLastAttendance} dias` : "—"}
              color={s.daysSinceLastAttendance != null && s.daysSinceLastAttendance >= INACTIVE_DAYS ? colors.error : undefined}
              sub={s.daysSinceLastAttendance != null && s.daysSinceLastAttendance >= INACTIVE_DAYS ? `inativo há mais de ${INACTIVE_DAYS} dias` : undefined}
              subColor={colors.error}
            />
          </Box>
          {s.specialtyStatus.length > 0 && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {s.specialtyStatus.map((st) => (
                <Chip
                  key={st.specialty}
                  size="small"
                  label={st.active ? `Em acompanhamento — ${st.specialty}` : `${st.outcomeName} — ${st.specialty} (${st.outcomeDate})`}
                  sx={{
                    fontWeight: 600,
                    bgcolor: st.active ? "#e8f6ec" : colors.backgroundAlt,
                    color: st.active ? colors.success : colors.textSecondary,
                    fontStyle: st.outcomeName === "Inativo" ? "italic" : "normal",
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: `1px solid ${colors.border}`, borderRadius: "8px" }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress size={28} /></Box>
        )}
        {!loading && error && (
          <Typography sx={{ color: colors.error, fontSize: "0.9rem", py: 2 }}>{error}</Typography>
        )}
        {!loading && !error && events.length === 0 && (
          <Typography sx={{ color: colors.textSecondary, textAlign: "center", py: 5, fontSize: "0.9rem" }}>
            Ainda não há marcos para este paciente.
          </Typography>
        )}

        {!loading && !error && events.length > 0 && (
          <Box component="ul" sx={{ position: "relative", listStyle: "none", m: 0, p: "0 0 0 30px",
            "&::before": { content: '""', position: "absolute", left: "9px", top: "8px", bottom: "8px", width: "2px", bgcolor: colors.border } }}>
            {events.map((e, i) => (
              <Box component="li" key={`${e.date}-${i}`} sx={{ position: "relative", pb: 2.5,
                "&::before": {
                  content: '""', position: "absolute", left: "-26px", top: "5px", width: 11, height: 11,
                  borderRadius: "50%", boxSizing: "border-box",
                  border: `2.5px solid ${KIND_COLOR[e.kind]}`,
                  bgcolor: e.kind === "sessions" ? colors.white : KIND_COLOR[e.kind],
                } }}>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: colors.textMuted, letterSpacing: "0.05em" }}>
                  {e.date}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: e.kind === "outcome" ? colors.success : colors.textPrimary }}>
                    {e.title}
                  </Typography>
                  {e.isManual && (
                    <Chip size="small" label="manual" sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, bgcolor: "#fff5e0", color: "#d98c00" }} />
                  )}
                  {e.outcomeId && (
                    <Tooltip title="Excluir marco">
                      <IconButton size="small" onClick={() => setToDelete(e)} sx={{ p: 0.25, color: colors.textMuted }}>
                        <Delete sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {(e.detail || e.professionalName) && (
                  <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary }}>
                    {/* professionalName só entra quando o detalhe ainda não o cita — antes o nome
                        aparecia duas vezes na mesma linha. */}
                    {[e.detail, e.professionalName && e.detail?.includes(e.professionalName) ? null : e.professionalName]
                      .filter(Boolean)
                      .join(" · ")}
                  </Typography>
                )}
                {!!e.children?.length && (
                  <Box sx={{ mt: 0.5 }}>
                    <Button
                      size="small"
                      onClick={() => setExpanded((p) => ({ ...p, [`${e.date}-${i}`]: !p[`${e.date}-${i}`] }))}
                      endIcon={expanded[`${e.date}-${i}`] ? <ExpandLess /> : <ExpandMore />}
                      sx={{ textTransform: "none", fontSize: "0.78rem", color: colors.primary, fontWeight: 600, p: 0, minWidth: 0 }}
                    >
                      {expanded[`${e.date}-${i}`] ? "ocultar detalhe" : "ver detalhe mês a mês"}
                    </Button>
                    <Collapse in={!!expanded[`${e.date}-${i}`]}>
                      <Box sx={{ pl: 1.5, borderLeft: `2px solid ${colors.border}`, mt: 0.5 }}>
                        {e.children!.map((c, ci) => (
                          <Box key={ci} sx={{ py: 0.4 }}>
                            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: colors.textPrimary }}>
                              {c.date} · {c.title}
                            </Typography>
                            <Typography sx={{ fontSize: "0.78rem", color: colors.textSecondary }}>
                              {[c.detail, c.professionalName].filter(Boolean).join(" · ")}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                )}
                {e.notes && (
                  <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary, borderLeft: `2px solid ${colors.border}`, pl: 1.5, mt: 0.75 }}>
                    {e.notes}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        {!loading && data?.hasMore && !from && !to && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
            <Button
              size="small"
              onClick={() => setMonthsBack((m) => m + 24)}
              sx={{ textTransform: "none", color: colors.primary, fontWeight: 600 }}
            >
              Ver histórico anterior
            </Button>
          </Box>
        )}
      </Paper>

      <TreatmentOutcomeModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={load}
        patientId={patientId}
        entityId={entityId}
        specialties={specialties}
      />

      <ConfirmModal
        isOpen={!!toDelete}
        title="Excluir marco"
        message={`Excluir "${toDelete?.title}"? O registro sai da linha do tempo, mas continua auditável.`}
        onConfirm={handleDelete}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
};

export default PatientTimeline;
